/**
 * 서비스 종료 신청
 * ---------------------------------------------------------------------------
 * ## 되돌릴 수 없는 일이다
 * 누르면 그 자리에서 정산되고 구독이 해지되고 로그인이 막힌다. 그래서
 * 화면이 하는 일의 절반은 **멈춰 세우는 것**이다 — 안내문을 열게 하고,
 * 정산될 금액을 보여 주고, 사유를 적게 한다.
 *
 * ## 안내문은 열어야 체크된다
 * 체크박스를 먼저 두면 읽지 않고 누른다. 체크박스를 누르면 **먼저 팝업이
 * 뜨고**, 닫아야 체크된다. 읽었다는 보장은 없지만 적어도 한 번은 지나간다.
 *
 * ## 금액을 먼저 보여 준다
 * 「마지막 청구 이후 오늘까지」가 즉시 청구된다. 백업을 고르면 그 비용도
 * 함께다. 누르기 전에 합계가 보여야 "종료했는데 왜 청구가 왔냐" 가 안 된다.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { useCodes } from '../../lib/codes';

const { Text, Paragraph } = Typography;

interface BackupOption {
  code: string;
  name: string;
  price: number;
}

interface TerminationOptions {
  backupOptions: BackupOption[];
  products: Array<{ productCode: string; productName: string; planName: string | null }>;
  purgeAfterDays: number;
  /** 종료 뒤 기업 관리자가 자료를 받으러 들어올 수 있는 일수 */
  graceLoginDays: number;
  /** 영구 삭제 며칠 전에 예고 메일이 오는가 */
  noticeLeadDays: number;
  notice: string;
  estimatedFinalAmount: number;
  /** 이번 정산에서 깎이는 환불 금액. estimatedFinalAmount 에는 이미 빠져 있다 */
  refundCreditAmount: number;
  settlementFrom: string | null;
  /** 연 약정을 중도에 끊어서 되돌려 내야 하는 금액 */
  commitmentClawback: number;
  commitmentBreakdown: Array<{ productName: string; amount: number }>;
}

interface TerminationRequest {
  requestId: string;
  status: string;
  reasonCode: string | null;
  reasonText: string;
  products: string[];
  backupOptions: BackupOption[];
  backupFee: number;
  noticeAgreedAt: string | null;
  purgeScheduledAt: string | null;
  finalInvoiceNo: string | null;
  finalAmount: number | null;
  requestedAt: string;
  requestedByName: string | null;
  completedAt: string | null;
  canceledAt: string | null;
}

const won = (n: number) => `${Math.round(n).toLocaleString('ko-KR')}원`;

const mb = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)}MB`
    : `${Math.max(1, Math.round(bytes / 1024))}KB`;

interface BackupArtifact {
  backupId: string;
  itemCode: string;
  itemName: string;
  partNo: number;
  partCount: number;
  status: string;
  fileName: string | null;
  byteSize: number;
  entryCount: number;
  errorMessage: string | null;
  builtAt: string | null;
  expiresAt: string | null;
  downloadCount: number;
}

const BACKUP_STATUS: Record<string, { text: string; color: string }> = {
  PENDING: { text: '대기 중', color: 'default' },
  BUILDING: { text: '만드는 중', color: 'processing' },
  READY: { text: '받으실 수 있습니다', color: 'green' },
  MANUAL: { text: '운영자 준비 중', color: 'gold' },
  FAILED: { text: '실패', color: 'red' },
  EXPIRED: { text: '보관 기간 종료', color: 'default' },
};

/**
 * 공통코드를 못 읽었을 때의 대비값. 서버(termination.service)의 기본값과
 * 같은 숫자다. 여기만 다르면 화면과 안내문이 서로 다른 일수를 말한다.
 *
 * 평소에는 쓰이지 않는다 — 카드는 options 를 다 읽은 뒤에 그려진다.
 */
const FALLBACK_PURGE_DAYS = 10;
const FALLBACK_GRACE_DAYS = 10;

/**
 * 제품을 끄면 실제로 무엇이 닫히는가.
 *
 * ## 「WORK&CRM」만 보고 고르게 두지 않는다
 * 목록에 제품명만 적어 두면, 고객은 자기가 매일 쓰는 화면 하나를 떠올리고
 * 체크한다. 실제로는 그 제품에 딸린 자료 전부가 함께 닫힌다. 무엇이
 * 사라지는지 **고르는 자리에서** 보여 줘야, 지우고 나서 알게 되는 일이
 * 없다.
 *
 * 문구는 Hub 가 들고 있다 — 제품 설명(platform.product.description)은 파는
 * 말이지 끄는 말이 아니라서 여기에 쓸 수 없다.
 */
const PRODUCT_IMPACT: Record<string, string> = {
  platform:
    '계정·조직도·역할과 권한, 올리신 파일, 기업 정보, 청구 이력까지 WORKnHub 전체가 닫힙니다. ' +
    '다른 제품은 모두 이 위에서 돌아가므로, 이것을 끄면 나머지도 함께 끝납니다.',
  eco:
    '대기 운영기록부·폐수 운영일지·자가측정 서류 등 WORK&ECO 에 쌓인 모든 일지와 첨부 서류, ' +
    '사업장·시설 정보가 닫힙니다. 법정 보존이 필요한 서류가 있으면 먼저 내려받아 주세요.',
  crm:
    '고객사·고객 직원·영업활동, 프로젝트와 업무, 전자결재 문서, 인사·재고·자원 자료 등 ' +
    'WORK&CRM 에 쌓인 모든 기록이 닫힙니다.',
};

/** Hub 를 끄면 제품이 돌아갈 바닥이 없어진다. 고를 때 함께 선택된다. */
const ROOT_PRODUCT = 'platform';

export function TerminationPanel() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const codes = useCodes(['TERMINATION_REASON']);

  const [products, setProducts] = useState<string[]>([]);
  const [backups, setBackups] = useState<string[]>([]);
  const [reasonCode, setReasonCode] = useState<string | undefined>();
  const [reasonText, setReasonText] = useState('');
  const [noticeAgreed, setNoticeAgreed] = useState(false);
  /** 메일로 받은 6자리 확인 코드 */
  const [confirmCode, setConfirmCode] = useState('');

  const options = useQuery<TerminationOptions, NormalizedError>({
    queryKey: ['tenant', 'termination', 'options'],
    queryFn: async () =>
      (await apiClient.get<TerminationOptions>('/tenant/termination/options')).data,
  });

  const history = useQuery<TerminationRequest[], NormalizedError>({
    queryKey: ['tenant', 'termination', 'history'],
    queryFn: async () =>
      (await apiClient.get<TerminationRequest[]>('/tenant/termination')).data,
  });

  /*
   * 백업은 종료 직후 뒤에서 만들어진다. 화면을 열어 둔 채 기다리는 사람이
   * 있으므로 다 만들어질 때까지 짧게 다시 묻는다. 끝나면 멈춘다 —
   * 영원히 도는 폴링은 서버에도 배터리에도 좋지 않다.
   */
  // 이름이 겹치지 않게 backupFiles 다 — 위의 backups 는 **고른 항목 코드**다.
  const backupFiles = useQuery<BackupArtifact[], NormalizedError>({
    queryKey: ['tenant', 'termination', 'backups'],
    queryFn: async () =>
      (await apiClient.get<BackupArtifact[]>('/tenant/termination/backups')).data,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((b) => b.status === 'PENDING' || b.status === 'BUILDING')
        ? 5000
        : false,
  });

  const download = useMutation({
    mutationFn: async (backupId: string) =>
      (
        await apiClient.post<{ url: string; fileName: string }>(
          `/tenant/termination/backups/${backupId}/download`,
          {},
        )
      ).data,
    onSuccess: (data) => {
      // 새 창을 열면 팝업 차단에 걸린다. 현재 창을 그 주소로 보낸다 —
      // 저장소가 Content-Disposition 을 붙여 주므로 화면은 그대로 있는다.
      window.location.href = data.url;
      void client.invalidateQueries({ queryKey: ['tenant', 'termination', 'backups'] });
    },
    onError: (err: NormalizedError) => message.error(errorText(err)),
  });

  /*
   * 1단계. 여기서는 **아무것도 해지되지 않는다** — 신청만 적히고 메일로
   * 코드가 간다. 화면도 그렇게 말해야 한다. "접수되었습니다" 로 끝내면
   * 고객은 끝난 줄 알고 창을 닫고, 코드를 넣지 않아 저절로 만료된다.
   */
  const submit = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post<{
          requestId: string;
          codeSentTo: string;
          codeTtlMinutes: number;
        }>('/tenant/termination', {
          reasonCode,
          reasonText: reasonText.trim(),
          products,
          backupCodes: backups,
          noticeAgreed: true,
        })
      ).data,
    onSuccess: async (data) => {
      message.info(`${data.codeSentTo} 로 확인 코드를 보냈습니다. 아직 종료되지 않았습니다.`);
      setNoticeAgreed(false);
      setConfirmCode('');
      await client.invalidateQueries({ queryKey: ['tenant', 'termination'] });
    },
    onError: (err: NormalizedError) => message.error(errorText(err)),
  });

  /** 2단계. 코드가 맞아야 실제로 정산·해지가 돈다. */
  const confirm = useMutation({
    mutationFn: async (requestId: string) =>
      (
        await apiClient.post<{ status: string }>(
          `/tenant/termination/${requestId}/confirm`,
          { code: confirmCode.trim() },
        )
      ).data,
    onSuccess: async () => {
      message.success('서비스 종료가 완료되었습니다. 안내 메일을 보내 드렸습니다.');
      setProducts([]);
      setBackups([]);
      setReasonText('');
      setConfirmCode('');
      await client.invalidateQueries({ queryKey: ['tenant', 'termination'] });
    },
    onError: (err: NormalizedError) => message.error(errorText(err)),
  });

  /** 되돌리기. 확인 전이면 없던 일이 된다. */
  const cancel = useMutation({
    mutationFn: async (requestId: string) =>
      (
        await apiClient.post<{ status: string }>(
          `/tenant/termination/${requestId}/cancel`,
          { reason: '신청자가 취소' },
        )
      ).data,
    onSuccess: async () => {
      message.success('종료 신청을 취소했습니다. 서비스는 그대로 쓰실 수 있습니다.');
      setConfirmCode('');
      await client.invalidateQueries({ queryKey: ['tenant', 'termination'] });
    },
    onError: (err: NormalizedError) => message.error(errorText(err)),
  });

  const o = options.data;
  const backupFee = (o?.backupOptions ?? [])
    .filter((b) => backups.includes(b.code))
    .reduce((sum, b) => sum + b.price, 0);
  /*
   * 약정 환수액도 합계에 넣는다.
   *
   * 빼 두면 화면에 적힌 「합계」와 실제로 긁히는 금액이 달라진다. 할인을
   * 받아 온 기업일수록 그 차이가 커서, 가장 놀랄 사람에게 가장 큰 차이를
   * 숨기는 꼴이 된다.
   */
  const clawback = o?.commitmentClawback ?? 0;
  const totalDue = (o?.estimatedFinalAmount ?? 0) + backupFee + clawback;

  /** 이미 종료가 끝났으면 다시 신청할 수 없다 */
  const completed = (history.data ?? []).find((r) => r.status === 'COMPLETED');

  /**
   * 코드 입력을 기다리는 신청.
   *
   * 이게 있으면 신청 서식을 숨기고 코드 칸만 보여 준다. 둘을 함께 두면
   * "신청을 또 해야 하나" 로 읽히고, 서버는 uq_termination_open 으로
   * 두 번째 신청을 거절하므로 영문 모를 오류만 본다.
   */
  const pending = (history.data ?? []).find((r) => r.status === 'REQUESTED');

  /*
   * 안내문을 띄우고, 닫으면 체크한다.
   * 체크박스를 먼저 누를 수 있게 두면 읽지 않고 지나간다.
   */
  const openNotice = () => {
    Modal.info({
      title: '서비스 종료 안내',
      width: 720,
      okText: '읽었습니다',
      content: (
        <div
          style={{
            maxHeight: '55vh',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            fontSize: 13,
            lineHeight: 1.9,
            marginTop: 12,
          }}
        >
          {o?.notice ?? '안내문을 불러오는 중입니다…'}
        </div>
      ),
      onOk: () => setNoticeAgreed(true),
    });
  };

  /*
   * 1단계 팝업.
   *
   * 「지금 일어나는 일」과 「코드를 넣어야 일어나는 일」을 갈라 적는다.
   * 섞어 두면 고객은 이 단추가 마지막인 줄 알고, 뒤에 코드 단계가 있다는
   * 것을 모른 채 창을 닫는다.
   */
  const confirmSubmit = () => {
    Modal.confirm({
      title: '서비스 종료를 신청하시겠습니까?',
      icon: <ExclamationCircleFilled style={{ color: '#cf1322' }} />,
      width: 580,
      okText: '신청하고 확인 코드 받기',
      okButtonProps: { danger: true },
      cancelText: '그만두기',
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.9 }}>
          <div style={{ marginBottom: 10 }}>
            지금 누르셔도 <b>아직 종료되지 않습니다.</b> 관리자 메일로 확인 코드를 보내
            드리고, 그 코드를 넣으셔야 처리됩니다. 코드를 넣지 않으시면 저절로 사라집니다.
          </div>
          <div style={{ fontWeight: 600 }}>코드를 넣으시면 그때 일어나는 일</div>
          <div>
            · <b>구성원이 로그인할 수 없게 됩니다.</b> 관리자만{' '}
            <b>{o?.graceLoginDays ?? FALLBACK_GRACE_DAYS}일</b> 동안 들어올 수 있습니다
            (백업 수령용)
          </div>
          <div>
            · <b>{won(totalDue)}</b> 이 즉시 청구됩니다 (부가세 별도)
          </div>
          <div>
            · 자료는 <b>{o?.purgeAfterDays ?? FALLBACK_PURGE_DAYS}일</b> 뒤 영구 삭제됩니다
          </div>
          <div>· 등록하신 결제수단의 자동결제가 중지됩니다</div>
        </div>
      ),
      onOk: () => submit.mutateAsync().then(() => undefined),
    });
  };

  /** 2단계 팝업. 여기가 진짜 마지막이다. */
  const confirmFinal = (requestId: string) => {
    Modal.confirm({
      title: '정말 서비스를 종료하시겠습니까?',
      icon: <ExclamationCircleFilled style={{ color: '#cf1322' }} />,
      width: 520,
      okText: '종료합니다',
      okButtonProps: { danger: true },
      cancelText: '취소',
      content: (
        <div style={{ fontSize: 13, lineHeight: 1.9 }}>
          누르시면 <b>그 자리에서 정산·결제·해지</b>가 진행됩니다. 이 단계 이후에는 화면에서
          되돌릴 수 없습니다.
        </div>
      ),
      onOk: () => confirm.mutateAsync(requestId).then(() => undefined),
    });
  };

  const canSubmit =
    products.length > 0 && reasonText.trim().length > 0 && noticeAgreed && !submit.isPending;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {completed && (
        <Alert
          type="warning"
          showIcon
          message="이미 서비스 종료가 완료되었습니다"
          description={
            <>
              {new Date(completed.completedAt!).toLocaleDateString('ko-KR')} 에 종료되었습니다.
              자료는 {completed.purgeScheduledAt
                ? new Date(completed.purgeScheduledAt).toLocaleDateString('ko-KR')
                : '-'}{' '}
              까지 보관됩니다. 지금 이 화면을 보고 계시다는 것은 관리자 로그인 유예 기간
              안이라는 뜻입니다 — 그날까지는 되돌릴 수 있으니 필요하시면 연락 주세요.
              신청하신 백업이 있으면 준비되는 대로 관리자 메일로 받으시는 방법을 보내 드립니다.
            </>
          }
        />
      )}

      {/*
        ## 받아 가는 자리

        신청하고 돈을 낸 자료를 **어디서 받는지**가 화면에 있어야 한다.
        예전에는 그 자리가 없어서 안내문도 "메일로 알려 드립니다" 까지만
        말할 수 있었다.

        종료 전에도 보여 준다(목록이 있으면). 다시 신청한 경우의 지난
        자료가 그대로 보이고, 받아 간 적이 있는지도 드러난다.
      */}
      {(backupFiles.data ?? []).length > 0 && (
        <Card
          size="small"
          title="신청하신 백업 자료"
          styles={{ header: { background: '#f6ffed' } }}
        >
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            아래에서 바로 내려받으실 수 있습니다. 압축 안의 파일은{' '}
            <b>올리실 때의 이름 그대로</b>이고, 제품별 폴더로 나뉘어 있습니다.
            자료는 <b>보관 기간이 끝나면 함께 삭제</b>되니 그 전에 받아 두세요.
            관리자 계정이 그때까지 로그인되는 이유가 이것입니다.
          </Paragraph>
          <Table
            size="small"
            rowKey="backupId"
            dataSource={backupFiles.data ?? []}
            pagination={false}
            columns={[
              {
                title: '항목',
                dataIndex: 'itemName',
                render: (name: string, r: BackupArtifact) => (
                  <span>
                    {name}
                    {r.partCount > 1 && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {' '}
                        ({r.partNo}/{r.partCount} 조각)
                      </Text>
                    )}
                  </span>
                ),
              },
              {
                title: '상태',
                dataIndex: 'status',
                width: 150,
                render: (s: string, r: BackupArtifact) => (
                  <>
                    <Tag color={BACKUP_STATUS[s]?.color ?? 'default'}>
                      {BACKUP_STATUS[s]?.text ?? s}
                    </Tag>
                    {r.errorMessage && (
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                        {r.errorMessage}
                      </Text>
                    )}
                  </>
                ),
              },
              {
                title: '크기',
                width: 130,
                render: (_: unknown, r: BackupArtifact) =>
                  r.status === 'READY' ? (
                    <Text style={{ fontSize: 12 }}>
                      {mb(r.byteSize)}
                      <Text type="secondary"> · {r.entryCount}건</Text>
                    </Text>
                  ) : (
                    <Text type="secondary">-</Text>
                  ),
              },
              {
                title: '받을 수 있는 날',
                width: 140,
                render: (_: unknown, r: BackupArtifact) =>
                  r.expiresAt ? (
                    <Text style={{ fontSize: 12 }}>
                      {new Date(r.expiresAt).toLocaleDateString('ko-KR')} 까지
                    </Text>
                  ) : (
                    <Text type="secondary">-</Text>
                  ),
              },
              {
                title: '',
                width: 110,
                render: (_: unknown, r: BackupArtifact) =>
                  r.status === 'READY' && r.entryCount > 0 ? (
                    <Button
                      size="small"
                      type="primary"
                      loading={download.isPending}
                      onClick={() => download.mutate(r.backupId)}
                    >
                      내려받기
                    </Button>
                  ) : r.status === 'READY' ? (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      담을 자료 없음
                    </Text>
                  ) : null,
              },
            ]}
          />
        </Card>
      )}

      {/*
        확인 코드를 기다리는 중.

        이 상태가 **되돌릴 수 있는 유일한 자리**다. 그래서 「취소」를 숨기지
        않고 코드 칸 바로 옆에 둔다 — 되돌리려고 전화를 걸게 만들 이유가 없다.
      */}
      {!completed && pending && (
        <Card
          size="small"
          title="확인 코드를 입력해 주세요"
          styles={{ header: { background: '#fffbe6' } }}
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="아직 종료되지 않았습니다"
            description={
              <span style={{ fontSize: 12 }}>
                {new Date(pending.requestedAt).toLocaleString('ko-KR')} 에 신청이 접수되어,
                신청하신 분의 메일로 <b>6자리 확인 코드</b>를 보내 드렸습니다. 코드를
                넣으셔야 정산·해지가 진행됩니다. 넣지 않으시면 유효 시간이 지난 뒤 저절로
                취소되며 <b>아무 일도 일어나지 않습니다.</b>
              </span>
            }
          />

          <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
            <Descriptions.Item label="끝낼 서비스">
              {pending.products.join(' · ') || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="백업 신청">
              {pending.backupOptions.length > 0
                ? `${pending.backupOptions.map((b) => b.name).join(' · ')} (${won(pending.backupFee)})`
                : '신청하지 않음'}
            </Descriptions.Item>
          </Descriptions>

          <Space.Compact style={{ width: '100%', maxWidth: 420 }}>
            <Input
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="메일로 받으신 6자리 숫자"
              maxLength={6}
              inputMode="numeric"
              onPressEnter={() => confirmCode.length === 6 && confirmFinal(pending.requestId)}
            />
            <Button
              danger
              type="primary"
              disabled={confirmCode.length !== 6 || confirm.isPending}
              loading={confirm.isPending}
              onClick={() => confirmFinal(pending.requestId)}
            >
              종료 확정
            </Button>
          </Space.Compact>

          <div style={{ marginTop: 12 }}>
            <Button
              onClick={() => cancel.mutate(pending.requestId)}
              loading={cancel.isPending}
            >
              신청 취소 — 그대로 계속 쓰겠습니다
            </Button>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
              취소하시면 아무것도 해지되지 않습니다. 코드를 못 받으셨으면 취소하신 뒤 다시
              신청해 주세요. 메일이 늦게 오는 경우가 있으니 스팸함도 확인해 보세요.
            </Text>
          </div>
        </Card>
      )}

      {!completed && !pending && (
        <Card
          size="small"
          title="서비스 종료 신청"
          loading={options.isLoading}
          styles={{ header: { background: '#fff1f0' } }}
        >
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            되돌리기 어려운 일입니다. 신청하시면 관리자 메일로 확인 코드가 가고,{' '}
            <b>그 코드를 넣으셔야</b> 정산·해지가 진행됩니다. 그때부터 구성원은 로그인할 수
            없게 되고, 자료는 {o?.purgeAfterDays ?? FALLBACK_PURGE_DAYS}일 동안 보관한 뒤
            삭제됩니다. 관리자 계정만 그 기간 동안 로그인이 유지되며, 신청하신 백업 자료를
            받으시는 용도입니다.
          </Paragraph>

          {/* 1. 끝낼 서비스 */}
          <div style={{ marginBottom: 20 }}>
            <Text strong>1. 끝낼 서비스를 고르세요</Text>
            <div style={{ marginTop: 8 }}>
              <Checkbox
                checked={products.length === (o?.products.length ?? 0) && products.length > 0}
                indeterminate={products.length > 0 && products.length < (o?.products.length ?? 0)}
                onChange={(e) =>
                  setProducts(e.target.checked ? (o?.products ?? []).map((p) => p.productCode) : [])
                }
                style={{ marginBottom: 8, fontWeight: 600 }}
              >
                전체 선택
              </Checkbox>
              {/*
                ## WORKnHub 를 고르면 나머지도 함께 고른다
                Hub 는 계정·조직·파일이다. 제품은 그 위에서 돈다. Hub 만 끄고
                WORK&CRM 은 남긴다는 선택은 **성립하지 않는데**, 체크박스가
                따로 서 있으면 그렇게 고를 수 있는 것처럼 보인다.

                서버도 같은 판단을 해야 안전하지만, 화면에서 먼저 묶어
                보여 주는 편이 낫다 — 고객이 "Hub 만 끄려 했는데 전부
                끝났다" 를 **누르기 전에** 알게 된다.

                반대 방향은 묶지 않는다. 제품 하나를 끈다고 Hub 까지 끌
                이유는 없다.
              */}
              <Checkbox.Group
                value={products}
                onChange={(v) => {
                  const picked = v as string[];
                  const all = (o?.products ?? []).map((p) => p.productCode);
                  setProducts(picked.includes(ROOT_PRODUCT) ? all : picked);
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingLeft: 24 }}
                options={(o?.products ?? []).map((p) => ({
                  value: p.productCode,
                  label: (
                    <span>
                      {p.planName ? `${p.productName} (${p.planName})` : p.productName}
                      {/*
                        "무엇을 잃는가" 를 적는 줄이다. 회색 11px 로 두면
                        아무도 읽지 않는다. 경고 색으로 올린다.
                      */}
                      {PRODUCT_IMPACT[p.productCode] && (
                        <Text
                          style={{
                            display: 'block',
                            fontSize: 12,
                            lineHeight: 1.6,
                            color: '#d4620a',
                          }}
                        >
                          {PRODUCT_IMPACT[p.productCode]}
                        </Text>
                      )}
                    </span>
                  ),
                }))}
              />
            </div>

            {products.includes(ROOT_PRODUCT) && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 10 }}
                message="WORKnHub 를 끝내면 모든 서비스가 함께 끝납니다"
                description={
                  <span style={{ fontSize: 12 }}>
                    WORKnHub 는 계정·조직도·권한·파일을 담고 있는 바탕입니다. 이것을 끄면
                    그 위에서 도는 WORK&ECO·WORK&CRM 도 쓸 수 없으므로 함께 선택했습니다.
                    제품 하나만 정리하시려는 것이라면 <b>WORKnHub 체크를 풀고</b> 그 제품만
                    고르세요.
                  </span>
                }
              />
            )}
          </div>

          {/* 2. 백업 */}
          <div style={{ marginBottom: 20 }}>
            <Text strong>2. 자료를 백업받으시겠습니까?</Text>
            {/*
              🔴 [문구 수정] 예전에는 "신청하지 않으셔도 보관 기간 안에는 직접
              내려받으실 수 있습니다" 라고 적혀 있었다. **사실이 아니다.**

              종료하면 구독이 해지되어 WORK&ECO·WORK&CRM 화면 자체가 열리지
              않는다. 자료는 서버에 남아 있지만 꺼낼 창구가 없다. 이 한 줄을
              읽고 백업을 건너뛴 고객은 보관 기간이 끝나고 나서야 그 사실을
              알게 되고, 그때는 자료가 이미 지워져 있다.

              유료 항목을 안 사도 된다고 말하는 문장이라 호의처럼 보이지만,
              되돌릴 수 없는 손해를 만드는 안내다.
            */}
            {/*
              여기는 **회색으로 두면 안 되는 문단**이다. 읽지 않고 지나가면
              자료를 영영 잃는다. 가장 중요한 한 줄만 붉게, 기한은 푸르게 둔다
              — 전부 색을 입히면 아무것도 강조되지 않는다.
            */}
            <Paragraph style={{ fontSize: 13, marginBottom: 8 }}>
              <Text type="danger" strong>
                종료하면 제품 화면이 닫혀 직접 내려받으실 수 없습니다.
              </Text>{' '}
              지금 쓰실 수 있는 자료는 <Text strong>종료 전에</Text> 내려받아 두시고, 그 밖의
              자료는 여기서 백업을 신청해 주세요. 고르신 항목을 받으실 수 있는 파일로 만들어
              드리며(원본 파일명 그대로), <Text style={{ color: '#2a78d6' }} strong>영업일 3~5일</Text>이
              걸립니다. 준비되면 관리자 메일로 받으시는 방법과 함께 알려 드립니다 — 종료 후{' '}
              <Text style={{ color: '#2a78d6' }} strong>
                {o?.graceLoginDays ?? FALLBACK_GRACE_DAYS}일
              </Text>{' '}
              동안 관리자 로그인을 열어 두는 이유입니다.
            </Paragraph>
            <Checkbox.Group
              value={backups}
              onChange={(v) => setBackups(v as string[])}
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
              options={(o?.backupOptions ?? []).map((b) => ({
                value: b.code,
                label: `${b.name} — ${won(b.price)}`,
              }))}
            />
          </div>

          {/* 3. 사유 */}
          <div style={{ marginBottom: 20 }}>
            <Text strong>3. 종료하시는 이유를 알려 주세요</Text>
            <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
              남겨 주신 말씀은 제품을 고치는 데 쓰겠습니다.
            </Paragraph>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Select
                value={reasonCode}
                onChange={setReasonCode}
                placeholder="사유를 고르세요"
                style={{ width: 240 }}
                options={codes.options('TERMINATION_REASON')}
              />
              <Input.TextArea
                rows={3}
                maxLength={1000}
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder="자세한 내용을 적어 주세요 (필수)"
              />
            </Space>
          </div>

          {/* 4. 정산 금액 */}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="종료하면 아래 금액이 즉시 청구됩니다"
            description={
              <Descriptions size="small" column={1} style={{ marginTop: 8 }}>
                <Descriptions.Item label={`이용료 정산 (${o?.settlementFrom ?? '-'} ~ 오늘)`}>
                  {won(o?.estimatedFinalAmount ?? 0)}
                </Descriptions.Item>
                {/*
                  환불 차감은 **이미 위 금액에서 빠져 있다.** 그래도 줄을
                  보여 준다 — 합계만 적힌 금액은 고객이 검산할 수 없고,
                  "왜 생각보다 적지?" 를 묻게 만든다.
                */}
                {(o?.refundCreditAmount ?? 0) > 0 && (
                  <Descriptions.Item label="환불 차감 (위 금액에 반영됨)">
                    <Text style={{ color: '#0ca30c' }} strong>
                      −{won(o?.refundCreditAmount ?? 0)}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                      이전에 돌려드리기로 한 금액을 이번 정산에서 깎았습니다.
                    </Text>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="백업 비용">{won(backupFee)}</Descriptions.Item>
                {clawback > 0 && (
                  <Descriptions.Item label="약정 할인 반환">
                    <Text type="danger">{won(clawback)}</Text>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                      {(o?.commitmentBreakdown ?? [])
                        .map((b) => `${b.productName} ${won(b.amount)}`)
                        .join(' · ')}{' '}
                      — 약정 기간을 채우기 전에 그만두시므로, 그동안 받으신 할인액을
                      되돌려 받습니다. 따로 떼는 위약금은 없습니다.
                    </Text>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="합계">
                  <strong style={{ fontSize: 15 }}>{won(totalDue)}</strong>{' '}
                  <Text type="secondary">(부가세 별도)</Text>
                </Descriptions.Item>
              </Descriptions>
            }
          />

          {/*
            일할 계산을 밝혀 둔다.

            "종료했는데 왜 돈을 또 내나" 가 해지 단계에서 가장 많이 나오는 말이다.
            남은 날짜만큼은 빼고 쓴 날짜만 받는다는 것을 **청구 금액 바로 밑에서**
            말해 주어야, 금액을 보고 바로 납득한다. 안내문 안에 적어 두면 늦다.
          */}
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message="사용한 기간 동안 비용이 청구 됩니다."
            description={
              <>
                이용료는 <b>날짜로 나누어</b> 계산합니다. 달 중간에 종료하시면 그달 전체가
                아니라 <b>마지막 청구 다음 날부터 오늘까지</b>만 청구됩니다.{' '}
                <Text style={{ color: '#0ca30c' }} strong>
                  남은 날짜분은 받지 않습니다.
                </Text>
                <br />
                <Text style={{ fontSize: 12 }}>
                  예) 한 달 55,000원인 요금제를 5일에 종료하면 55,000 × 5/31 ={' '}
                  <Text style={{ color: '#2a78d6' }} strong>
                    8,871원
                  </Text>
                  . 저장 용량·트래픽 초과분은 실제로 쓴 양 그대로 더해집니다.
                </Text>
              </>
            }
          />

          {/* 5. 안내문 */}
          <div
            style={{
              border: '1px solid #ffccc7',
              background: '#fff2f0',
              borderRadius: 8,
              padding: '12px 14px',
              marginBottom: 16,
            }}
          >
            <Checkbox
              checked={noticeAgreed}
              onChange={(e) => {
                // 끌 때는 그냥 끄고, 켤 때는 먼저 안내문을 띄운다.
                if (!e.target.checked) {
                  setNoticeAgreed(false);
                  return;
                }
                openNotice();
              }}
            >
              <b>서비스 종료 안내</b>를 읽고 이해했습니다. <Text type="danger">(필수)</Text>
            </Checkbox>
            <div style={{ marginTop: 6, paddingLeft: 24 }}>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={openNotice}>
                안내문 다시 보기
              </Button>
            </div>
          </div>

          <Button
            danger
            type="primary"
            disabled={!canSubmit}
            loading={submit.isPending}
            onClick={confirmSubmit}
          >
            종료 신청하고 확인 코드 받기
          </Button>
          {!canSubmit && !submit.isPending && (
            <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
              {products.length === 0
                ? '끝낼 서비스를 고르세요'
                : !reasonText.trim()
                  ? '종료 사유를 적어 주세요'
                  : '안내문에 동의해 주세요'}
            </Text>
          )}
        </Card>
      )}

      {(history.data ?? []).length > 0 && (
        <Card size="small" title="신청 이력">
          <Table<TerminationRequest>
            rowKey="requestId"
            size="small"
            pagination={false}
            dataSource={history.data ?? []}
            columns={[
              {
                title: '신청 일시',
                dataIndex: 'requestedAt',
                width: 160,
                render: (v: string) => new Date(v).toLocaleString('ko-KR'),
              },
              {
                title: '상태',
                dataIndex: 'status',
                width: 100,
                render: (v: string) => (
                  <Tag color={v === 'COMPLETED' ? 'red' : v === 'CANCELED' ? 'default' : 'orange'}>
                    {v === 'COMPLETED' ? '종료됨' : v === 'CANCELED' ? '철회' : '접수'}
                  </Tag>
                ),
              },
              {
                title: '끝낸 서비스',
                dataIndex: 'products',
                render: (v: string[]) => v.join(', '),
              },
              {
                title: '정산',
                key: 'final',
                width: 160,
                align: 'right',
                render: (_, r) =>
                  r.finalAmount === null ? (
                    <Text type="secondary">—</Text>
                  ) : (
                    <Space direction="vertical" size={0}>
                      <span>{won(r.finalAmount)}</span>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {r.finalInvoiceNo}
                      </Text>
                    </Space>
                  ),
              },
              {
                title: '삭제 예정',
                dataIndex: 'purgeScheduledAt',
                width: 120,
                render: (v: string | null) =>
                  v ? new Date(v).toLocaleDateString('ko-KR') : '—',
              },
            ]}
          />
        </Card>
      )}
    </Space>
  );
}
